import { useState, useEffect } from "react";
import clsx from "clsx";
import { Button } from "./atoms";
import { fetchStores, type StoreResponse } from "../api/stores";
import { fetchStoreDepartments, type DepartmentResponse } from "../api/departments";
import { fetchEmploymentTypes, type EmploymentTypeResponse } from "../api/employmentTypes";
import { recordUserAction } from "../utils/awsRum";

/**
 * 従業員フォームデータの型定義（API送信用）
 */
export interface EmployeeFormData {
    code: string;
    name: string;
    nickname: string;
    birth_date: string | null; // YYYY-MM-DD形式
    is_foreign_national: boolean;
    memo: string;
    employment_status: string;
    store_id: number;
    department_id: number;
    employment_type_id: number;
    permission_level: number;
}

/**
 * 内部フォーム状態の型定義
 */
interface InternalFormData {
    code: string;
    name: string;
    nickname: string;
    birth_year: string;
    birth_month: string;
    birth_day: string;
    is_foreign_national: boolean;
    memo: string;
    employment_status: string;
    store_id: string;
    department_id: string;
    employment_type_id: string;
    permission_level: string;
}

interface EmployeeFormProps {
    onCancel: () => void;
    onNext: () => void;
    initialData?: Partial<EmployeeFormData>;
    onFormDataChange?: (data: EmployeeFormData) => void;
    /** 必須チェックで未入力だったフィールドキー（API 側の snake_case）。空の間は赤枠表示 */
    invalidFields?: string[];
}

/**
 * 年の選択肢を生成（1950年〜現在）
 */
const generateYears = (): string[] => {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let year = currentYear; year >= 1950; year--) {
        years.push(String(year));
    }
    return years;
};

/**
 * 月の選択肢を生成（1〜12）
 */
const generateMonths = (): string[] => {
    return Array.from({ length: 12 }, (_, i) => String(i + 1));
};

/**
 * 日の選択肢を生成（1〜31）
 */
const generateDays = (): string[] => {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
};

/**
 * 在籍状況の選択肢
 */
const EMPLOYMENT_STATUS_OPTIONS = [
    { value: "在籍中", label: "在籍中" },
    { value: "休職", label: "休職" },
    { value: "退職", label: "退職" },
    { value: "復職予定", label: "復職予定" },
];

/**
 * 権限レベルの選択肢
 */
const PERMISSION_LEVEL_OPTIONS = [
    { value: "20", label: "部門長" },
    { value: "40", label: "店長" },
    { value: "50", label: "課管理者" },
    { value: "80", label: "部門管理者" },
    { value: "100", label: "エリア管理者" },
];

/**
 * 日付文字列から年月日を分離
 */
const parseBirthDate = (dateStr: string | null | undefined): { year: string; month: string; day: string } => {
    if (!dateStr) {
        return { year: "", month: "", day: "" };
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
        return { year: "", month: "", day: "" };
    }
    return {
        year: String(date.getFullYear()),
        month: String(date.getMonth() + 1),
        day: String(date.getDate()),
    };
};

/**
 * 従業員登録・編集フォームコンポーネント
 */
const EmployeeForm = ({ onCancel, onNext, initialData, onFormDataChange, invalidFields = [] }: EmployeeFormProps) => {
    const birthDate = parseBirthDate(initialData?.birth_date);

    const [formData, setFormData] = useState<InternalFormData>({
        code: initialData?.code || "",
        name: initialData?.name || "",
        nickname: initialData?.nickname || "",
        birth_year: birthDate.year || "",
        birth_month: birthDate.month || "",
        birth_day: birthDate.day || "",
        is_foreign_national: initialData?.is_foreign_national ?? false,
        memo: initialData?.memo || "",
        employment_status: initialData?.employment_status || "在籍中",
        store_id: initialData?.store_id ? String(initialData.store_id) : "",
        department_id: initialData?.department_id ? String(initialData.department_id) : "",
        employment_type_id: initialData?.employment_type_id ? String(initialData.employment_type_id) : "",
        permission_level: String(initialData?.permission_level || 1),
    });

    const [storeOptions, setStoreOptions] = useState<StoreResponse[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<DepartmentResponse[]>([]);
    const [employmentTypeOptions, setEmploymentTypeOptions] = useState<EmploymentTypeResponse[]>([]);

    /**
     * 必須フィールドが未入力のままかを判定する。
     * invalidFields（親から受け取ったキー）に含まれ、かつ現在の値が空/0 のとき true。
     */
    const isFieldInvalid = (key: keyof InternalFormData): boolean => {
        if (!invalidFields.includes(key)) return false;
        const value = formData[key];
        if (typeof value === "boolean") return false;
        return !value || !String(value).trim();
    };

    // 店舗一覧を取得
    useEffect(() => {
        const loadStores = async () => {
            try {
                const response = await fetchStores();
                setStoreOptions(response.items);
            } catch (err) {
                console.error("Failed to fetch stores:", err);
            }
        };
        loadStores();
    }, []);

    // 雇用形態一覧を取得
    useEffect(() => {
        const loadEmploymentTypes = async () => {
            try {
                const response = await fetchEmploymentTypes();
                setEmploymentTypeOptions(response.items);
            } catch (err) {
                console.error("Failed to fetch employment types:", err);
            }
        };
        loadEmploymentTypes();
    }, []);

    // 選択中の店舗に応じた部門一覧を取得
    useEffect(() => {
        const storeId = parseInt(formData.store_id, 10);
        if (!storeId) {
            setDepartmentOptions([]);
            return;
        }

        const loadDepartments = async () => {
            try {
                const response = await fetchStoreDepartments(storeId);
                setDepartmentOptions(response.items);
            } catch (err) {
                console.error("Failed to fetch departments:", err);
            }
        };
        loadDepartments();
    }, [formData.store_id]);

    // フォームデータが変更されたら親に通知
    useEffect(() => {
        if (onFormDataChange) {
            const apiData = convertToApiFormat(formData);
            onFormDataChange(apiData);
        }
    }, [formData, onFormDataChange]);

    const handleInputChange = (field: keyof InternalFormData, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // 内部フォーマットをAPI送信用に変換
    const convertToApiFormat = (data: InternalFormData): EmployeeFormData => {
        let birthDateStr: string | null = null;
        if (data.birth_year && data.birth_month && data.birth_day) {
            const month = data.birth_month.padStart(2, "0");
            const day = data.birth_day.padStart(2, "0");
            birthDateStr = `${data.birth_year}-${month}-${day}`;
        }

        return {
            code: data.code,
            name: data.name,
            nickname: data.nickname,
            birth_date: birthDateStr,
            is_foreign_national: data.is_foreign_national,
            memo: data.memo,
            employment_status: data.employment_status,
            store_id: parseInt(data.store_id, 10) || 0,
            department_id: parseInt(data.department_id, 10) || 0,
            employment_type_id: parseInt(data.employment_type_id, 10) || 0,
            permission_level: parseInt(data.permission_level, 10) || 1,
        };
    };

    return (
        <div className="flex-1 flex bg-white rounded-lg overflow-hidden">
                {/* 左カラム */}
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-auto p-6">
                        <div className="space-y-6">
                            <div className="text-xs text-red-500">「*」は登録必須項目です。</div>
                            {/* ID */}
                            <FormField label="ID" required>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => handleInputChange("code", e.target.value)}
                                    placeholder="00000000000000"
                                    className={clsx("form-input-field", isFieldInvalid("code") && "border-red-500")}
                                />
                            </FormField>

                            {/* 名前 */}
                            <FormField label="名前" required>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                    placeholder="田中太郎"
                                    className={clsx("form-input-field", isFieldInvalid("name") && "border-red-500")}
                                />
                            </FormField>

                            {/* 呼び名 */}
                            <FormField label="呼び名">
                                <input
                                    type="text"
                                    value={formData.nickname}
                                    onChange={(e) => handleInputChange("nickname", e.target.value)}
                                    placeholder="田中"
                                    className="form-input-field"
                                />
                            </FormField>

                            {/* 生年月日 */}
                            <FormField label="生年月日">
                                <div className="flex items-center gap-2">
                                    <select
                                        value={formData.birth_year}
                                        onChange={(e) => { recordUserAction("select_changed", { select: "birth_year", value: e.target.value, page: "employee_form" }); handleInputChange("birth_year", e.target.value); }}
                                        className="form-select-field w-28"
                                    >
                                        <option value="">--</option>
                                        {generateYears().map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-gray-600">年</span>
                                    <select
                                        value={formData.birth_month}
                                        onChange={(e) => { recordUserAction("select_changed", { select: "birth_month", value: e.target.value, page: "employee_form" }); handleInputChange("birth_month", e.target.value); }}
                                        className="form-select-field w-20"
                                    >
                                        <option value="">--</option>
                                        {generateMonths().map((month) => (
                                            <option key={month} value={month}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-gray-600">月</span>
                                    <select
                                        value={formData.birth_day}
                                        onChange={(e) => { recordUserAction("select_changed", { select: "birth_day", value: e.target.value, page: "employee_form" }); handleInputChange("birth_day", e.target.value); }}
                                        className="form-select-field w-20"
                                    >
                                        <option value="">--</option>
                                        {generateDays().map((day) => (
                                            <option key={day} value={day}>
                                                {day}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-gray-600">日</span>
                                </div>
                            </FormField>

                            {/* 外国籍 */}
                            <FormField label="外国籍">
                                <div className="flex items-center gap-6">
                                    <RadioButton
                                        name="foreign"
                                        value="yes"
                                        checked={formData.is_foreign_national}
                                        onChange={() => handleInputChange("is_foreign_national", true)}
                                        label="該当する"
                                    />
                                    <RadioButton
                                        name="foreign"
                                        value="no"
                                        checked={!formData.is_foreign_national}
                                        onChange={() => handleInputChange("is_foreign_national", false)}
                                        label="該当しない"
                                    />
                                </div>
                            </FormField>

                            {/* メモ */}
                            <FormField label="メモ">
                                <textarea
                                    value={formData.memo}
                                    onChange={(e) => handleInputChange("memo", e.target.value)}
                                    rows={6}
                                    className="form-input-field resize-none"
                                    placeholder="テキストテキストテキスト..."
                                />
                            </FormField>
                        </div>
                    </div>
                </div>

                {/* 区切り線（中央・上下余白付き） */}
                <div className="w-px bg-gray-200 my-4" />

                {/* 右カラム */}
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-auto p-6">
                        <div className="space-y-6">
                            {/* 在籍状況 */}
                            <FormField label="在籍状況">
                                <select
                                    value={formData.employment_status}
                                    onChange={(e) => { recordUserAction("select_changed", { select: "employment_status", value: e.target.value, page: "employee_form" }); handleInputChange("employment_status", e.target.value); }}
                                    className="form-select-field w-full"
                                >
                                    {EMPLOYMENT_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            {/* 店舗 */}
                            <FormField label="店舗" required>
                                <select
                                    value={formData.store_id}
                                    onChange={(e) => {
                                        recordUserAction("select_changed", { select: "store", value: e.target.value, page: "employee_form" });
                                        handleInputChange("store_id", e.target.value);
                                        // 店舗変更時に部門をリセット
                                        handleInputChange("department_id", "");
                                    }}
                                    className={clsx("form-select-field w-full", isFieldInvalid("store_id") && "border-red-500")}
                                >
                                    <option value="">選択してください</option>
                                    {storeOptions.map((store) => (
                                        <option key={store.id} value={String(store.id)}>
                                            {store.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            {/* 部門 */}
                            <FormField label="部門" required>
                                <select
                                    value={formData.department_id}
                                    onChange={(e) => { recordUserAction("select_changed", { select: "department", value: e.target.value, page: "employee_form" }); handleInputChange("department_id", e.target.value); }}
                                    className={clsx("form-select-field w-full", isFieldInvalid("department_id") && "border-red-500")}
                                    disabled={!formData.store_id}
                                >
                                    <option value="">選択してください</option>
                                    {departmentOptions.map((dept) => (
                                        <option key={dept.id} value={String(dept.id)}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            {/* 雇用形態 */}
                            <FormField label="雇用形態" required>
                                <select
                                    value={formData.employment_type_id}
                                    onChange={(e) => { recordUserAction("select_changed", { select: "employment_type", value: e.target.value, page: "employee_form" }); handleInputChange("employment_type_id", e.target.value); }}
                                    className={clsx("form-select-field w-full", isFieldInvalid("employment_type_id") && "border-red-500")}
                                >
                                    <option value="">雇用形態を選択してください</option>
                                    {employmentTypeOptions.map((opt) => (
                                        <option key={opt.id} value={String(opt.id)}>
                                            {opt.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            {/* 権限 */}
                            <FormField label="権限">
                                <select
                                    value={formData.permission_level}
                                    onChange={(e) => { recordUserAction("select_changed", { select: "permission_level", value: e.target.value, page: "employee_form" }); handleInputChange("permission_level", e.target.value); }}
                                    className="form-select-field w-full"
                                >
                                    {PERMISSION_LEVEL_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                        </div>
                    </div>

                    {/* フッターボタン */}
                    <div className="flex justify-end gap-4 px-6 py-4">
                        <Button variant="outline" size="md" onClick={onCancel} className="px-8">
                            キャンセル
                        </Button>
                        <Button variant="primary" size="md" onClick={onNext} className="px-8">
                            2.契約情報（個人）の設定へ進む
                        </Button>
                    </div>
                </div>
            </div>
    );
};

/**
 * フォームフィールドラッパー
 */
interface FormFieldProps {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}

const FormField = ({ label, required = false, children }: FormFieldProps) => (
    <div className="flex items-start gap-6">
        <label className="w-24 text-sm text-gray-700 pt-2 flex-shrink-0">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

/**
 * ラジオボタンコンポーネント
 */
interface RadioButtonProps {
    name: string;
    value: string;
    checked: boolean;
    onChange: () => void;
    label: string;
}

const RadioButton = ({ name, value, checked, onChange, label }: RadioButtonProps) => (
    <label className="flex items-center gap-2 cursor-pointer">
        <div
            className={clsx(
                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                checked ? "border-brand-500" : "border-gray-300"
            )}
        >
            {checked && <div className="w-2.5 h-2.5 rounded-full bg-brand-500" />}
        </div>
        <input
            type="radio"
            name={name}
            value={value}
            checked={checked}
            onChange={onChange}
            className="sr-only"
        />
        <span className="text-sm text-gray-700">{label}</span>
    </label>
);

export default EmployeeForm;
